"use client";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useCallback } from "react";
import { deserializeURLSearchParams, validateParams } from "@/lib/utils";
import z from "zod";
import { getActivityLogListRequestSchema } from "@/lib/schemas/activity-log";
import type {
  ActivityActionTypeValue,
  ActivityPathValue,
  ActivityResultValue,
} from "@/types/activity-log";
import { ActivityScope } from "@/lib/activity-log-scop";

export type ActivityLogFilterParams = z.infer<
  typeof getActivityLogListRequestSchema
>;

const PAGINATION_SIZES = [10, 25, 50, 100] as const;

export function useActivityLogFilters(scope: ActivityScope) {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();

  const parsed = validateParams(
    getActivityLogListRequestSchema,
    deserializeURLSearchParams(searchParams.toString()),
  );

  const filters: ActivityLogFilterParams = {
    scope,
    dateFrom: parsed.dateFrom ?? null,
    dateTo: parsed.dateTo ?? null,
    path: (parsed.path as ActivityPathValue) ?? null,
    kvps: parsed.kvps ?? null,
    actionType: (parsed.actionType as ActivityActionTypeValue) ?? null,
    result: (parsed.result as ActivityResultValue) ?? null,
    q: parsed.q ?? null,
    p: PAGINATION_SIZES.includes(parsed.s as (typeof PAGINATION_SIZES)[number])
      ? parsed.p
      : 1,
    s: PAGINATION_SIZES.includes(parsed.s as (typeof PAGINATION_SIZES)[number])
      ? (parsed.s as (typeof PAGINATION_SIZES)[number])
      : 10,
  };

  const setFilters = useCallback(
    (updates: Partial<ActivityLogFilterParams>) => {
      const params = new URLSearchParams(searchParams.toString());
      if (!params.has("t")) params.set("t", "logs");

      if (updates.dateFrom !== undefined) {
        if (updates.dateFrom) params.set("dateFrom", updates.dateFrom);
        else params.delete("dateFrom");
      }
      if (updates.dateTo !== undefined) {
        if (updates.dateTo) params.set("dateTo", updates.dateTo);
        else params.delete("dateTo");
      }
      if (updates.path !== undefined) {
        if (updates.path) params.set("path", updates.path);
        else params.delete("path");
      }
      if (updates.kvps !== undefined) {
        if (updates.kvps) params.set("kvps", updates.kvps);
        else params.delete("kvps");
      }
      if (updates.actionType !== undefined) {
        if (updates.actionType) params.set("actionType", updates.actionType);
        else params.delete("actionType");
      }
      if (updates.result !== undefined) {
        if (updates.result) params.set("result", updates.result);
        else params.delete("result");
      }
      if (updates.q !== undefined) {
        if (updates.q) params.set("q", updates.q);
        else params.delete("q");
      }
      if (updates.p !== undefined) params.set("p", String(updates.p));
      else params.set("p", "1");
      if (updates.s !== undefined) params.set("s", String(updates.s));

      router.replace(`${pathname}?${params.toString()}`, { scroll: false });
    },
    [pathname, router, searchParams],
  );

  return { filters, setFilters, paginationSizes: PAGINATION_SIZES };
}
